/**
 * @file Onglet « Feux d'artifice » de la boutique du hub : feux cosmetiques (du classique aux grands bouquets), achat gemmes / Rubis, equipement.
 */
import type { FireworkDef } from '@config/types';
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { confirmAsk, send } from '../../core/feedback';
import { fireworkPreview } from '../../core/icons';
import { balanceOf, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';

interface CardView {
  fw: FireworkDef;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement;
}

export class FireworkPane extends Component {
  private readonly cards: CardView[] = [];

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'fw-pane');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    const grid = h('div', { class: 'aura-grid' });
    for (const fw of this.ctx.config.fireworks) {
      const state = h('div', { class: 'skin-state' });
      const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, fw.cost));
      const root = h(
        'button',
        { class: 'skin-card aura-card', type: 'button', 'data-ui-block': true, 'data-firework': fw.id, onclick: () => void this.activate(fw) },
        h('div', { class: 'aura-view fw-view' }, fireworkPreview(fw.style, this.ctx.config.ammoSkins.find((a) => a.id === this.ctx.sim.data.ammoSkins.equipped.single)?.style)),
        h('div', { class: 'skin-name' }, t.name(fw.name)),
        state,
        costs,
      );
      this.cards.push({ fw, root, state, costs });
      grid.append(root);
    }
    this.el.append(h('p', { class: 'hint' }, t.t('fireworks.hint')), grid);
    this.update(0);
  }

  private async activate(fw: FireworkDef): Promise<void> {
    const { data } = this.ctx.sim;
    if (data.fireworks.owned.includes(fw.id)) {
      if (data.fireworks.equipped !== fw.id) void send(this.ctx, { type: 'firework.equip', id: fw.id });
      return;
    }
    if (fw.cost.dia !== undefined && !(await confirmAsk('skins.confirmGems'))) return;
    const r = send(this.ctx, { type: 'firework.buy', id: fw.id });
    if (r.ok) void send(this.ctx, { type: 'firework.equip', id: fw.id });
  }

  override update(_dt: number): void {
    const { data } = this.ctx.sim;
    const { i18n } = this.ctx;
    for (const c of this.cards) {
      const owned = data.fireworks.owned.includes(c.fw.id);
      const equipped = data.fireworks.equipped === c.fw.id;
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      const affordable = (Object.keys(c.fw.cost) as WalletId[]).every((id) => {
        const v = c.fw.cost[id];
        return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
      });
      toggleClass(c.root, 'disabled', !owned && !affordable);
    }
  }
}
